import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { ArrowRight, ClipboardList, Eye, Loader2 } from 'lucide-react';
import type { WorkLogEntry, WorkLogPage } from './types';
import {
  entryActor,
  formatEntryWhen,
  formatTrackedSince,
  humanizeField,
  shownChanges,
  type WorkLogValueFormatter,
} from './workLogFormat';

export interface EntityWorkLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fetches one page of the log, oldest first. Called with no cursor each time
   * the dialog opens, so the reader always sees edits made since they last
   * looked, then with the previous page's `next_cursor` for each further page.
   * Memoize it (`useCallback`) so a parent re-render does not refetch.
   *
   * Anything else the request needs belongs in this closure. A log opened from
   * a deleted record must ask for `include_deleted` here, or it fails to load.
   */
  load: (cursor?: string) => Promise<WorkLogPage>;
  title?: string;
  description?: ReactNode;
  /**
   * Display names for the record's columns. A field with no entry falls back
   * to its own name, humanized, so a new backend column still reads sensibly
   * without a frontend change.
   */
  fieldLabels?: Record<string, string>;
  /** The record's own value display rules (enum labels, amounts). */
  formatValue?: WorkLogValueFormatter;
  /**
   * The day (`YYYY-MM-DD`) this record's changes started being logged. When
   * set, the log says so, so a record older than its log does not look broken
   * for having no early history.
   */
  trackedSince?: string;
  /** Opens the record as it stood at an entry. Adds a View column for entries that support it. */
  onViewRevision?: (asOf: string) => void;
}

const EMPTY_VALUE = '(empty)';

const ValueText: React.FC<{ value: string | null }> = ({ value }) =>
  value === null ? <span className='italic'>{EMPTY_VALUE}</span> : <>{value}</>;

const ActorCell: React.FC<{ entry: WorkLogEntry }> = ({ entry }) => {
  const actor = entryActor(entry);
  if (actor.kind === 'person') return <>{actor.name}</>;
  return (
    <div className='flex flex-col items-start gap-0.5'>
      <span className='inline-flex items-center whitespace-nowrap rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-700'>
        {actor.kind === 'ai' ? 'AI' : 'System'}
      </span>
      {actor.source ? (
        <span className='text-xs text-muted-foreground'>{actor.source}</span>
      ) : null}
      {actor.initiator ? (
        <span className='text-xs text-muted-foreground'>
          after {actor.initiator}
        </span>
      ) : null}
    </div>
  );
};

const ChangeList: React.FC<{
  entry: WorkLogEntry;
  fieldLabels?: Record<string, string>;
  formatValue?: WorkLogValueFormatter;
}> = ({ entry, fieldLabels, formatValue }) => {
  const changes = shownChanges(entry, formatValue);
  if (changes.length === 0) return null;
  return (
    <ul className='mt-1 space-y-0.5 text-xs'>
      {changes.map(({ field, oldValue, newValue }) => (
        <li key={field} className='break-words'>
          <span className='font-medium'>
            {humanizeField(field, fieldLabels)}:
          </span>{' '}
          <del className='text-muted-foreground'>
            <span className='sr-only'>was </span>
            <ValueText value={oldValue} />
          </del>{' '}
          <ArrowRight
            className='inline h-3 w-3 text-muted-foreground'
            aria-hidden
          />{' '}
          <span>
            <span className='sr-only'>now </span>
            <ValueText value={newValue} />
          </span>
        </li>
      ))}
    </ul>
  );
};

/**
 * Who changed what, and when, for one record.
 *
 * Generic on purpose: the NOA is the first caller, and any record with a
 * `WorkLogResponse` endpoint reuses it by passing its own `load`, labels and
 * value rules. Grouping, amendments, system actors and paging all come from
 * the response, so a record that has none of them renders as a plain list.
 */
export const EntityWorkLogDialog: React.FC<EntityWorkLogDialogProps> = ({
  open,
  onOpenChange,
  load,
  title = 'Work log',
  description = 'Edits to this record, oldest first.',
  fieldLabels,
  formatValue,
  trackedSince,
  onViewRevision,
}) => {
  const [entries, setEntries] = useState<WorkLogEntry[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  // Bumped on every open (and on close), so a page that arrives after the
  // dialog closed or restarted is dropped instead of appended.
  const generation = useRef(0);
  // Set while a further page is in flight, so a second click before the
  // button disables cannot fetch the same page twice.
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    generation.current += 1;
    if (!open) return;
    const current = generation.current;
    setLoading(true);
    setError(null);
    setEntries(null);
    setNextCursor(null);
    setMoreError(null);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    load()
      .then(page => {
        if (generation.current !== current) return;
        setEntries(page.work_log ?? []);
        setNextCursor(page.next_cursor ?? null);
      })
      .catch(err => {
        if (generation.current !== current) return;
        console.error('Failed to load work log:', err);
        setError('Unable to load the work log. Please try again.');
      })
      .finally(() => {
        if (generation.current === current) setLoading(false);
      });
  }, [open, load]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMoreRef.current) return;
    const current = generation.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setMoreError(null);
    load(nextCursor)
      .then(page => {
        if (generation.current !== current) return;
        setEntries(prev => [...(prev ?? []), ...(page.work_log ?? [])]);
        setNextCursor(page.next_cursor ?? null);
      })
      .catch(err => {
        if (generation.current !== current) return;
        console.error('Failed to load more of the work log:', err);
        setMoreError('Unable to load more of the work log. Please try again.');
      })
      .finally(() => {
        if (generation.current !== current) return;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [load, nextCursor]);

  const showView =
    !!onViewRevision && (entries ?? []).some(entry => entry.revision_as_of);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-[760px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ClipboardList className='h-5 w-5 text-primary' />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-auto pr-1'>
          {trackedSince ? (
            <p className='pb-2 text-xs text-muted-foreground'>
              Tracks changes since {formatTrackedSince(trackedSince)}.
            </p>
          ) : null}
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : !entries || entries.length === 0 ? (
            <div className='py-6 text-sm text-muted-foreground'>
              {trackedSince
                ? 'No edits recorded yet.'
                : 'No edits recorded yet. The work log tracks changes made after it was introduced, so earlier edits do not appear here.'}
            </div>
          ) : (
            <>
              <Table className='min-w-[560px]'>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    {showView ? <TableHead className='w-0' /> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, index) => {
                    const count = entry.count ?? 1;
                    const asOf = entry.revision_as_of;
                    return (
                      <TableRow
                        key={`${entry.action}-${entry.edited_at}-${index}`}
                      >
                        <TableCell className='align-top'>
                          <ActorCell entry={entry} />
                        </TableCell>
                        <TableCell className='align-top'>
                          {entry.label}
                          {count > 1 ? ` (x${count})` : ''}
                          {entry.amended ? (
                            <span
                              className='ml-2 inline-flex items-center whitespace-nowrap rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800'
                              title='Made after the certification period closed'
                            >
                              Amended
                            </span>
                          ) : null}
                          {entry.detail ? (
                            <span className='block text-xs text-muted-foreground'>
                              {entry.detail}
                            </span>
                          ) : null}
                          <ChangeList
                            entry={entry}
                            fieldLabels={fieldLabels}
                            formatValue={formatValue}
                          />
                        </TableCell>
                        <TableCell className='align-top'>
                          {formatEntryWhen(entry)}
                        </TableCell>
                        {showView ? (
                          <TableCell className='align-top text-right'>
                            {asOf && onViewRevision ? (
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => onViewRevision(asOf)}
                                aria-label='View the record at this point'
                              >
                                <Eye className='h-4 w-4' />
                                View
                              </Button>
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {moreError ? (
                <div className='pt-2 text-sm text-destructive'>{moreError}</div>
              ) : null}
              {nextCursor ? (
                <div className='flex justify-center pt-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : null}
                    Show more
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
